import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import DynamicForm from "../common/DynamicForm";
import { AlertDialog } from "../common/AlertDailog";
import FormFields from "./vendorFields.json";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useAppSelector, AppState, useAppDispatch } from "../../store";
import { vendorSchema } from "../../config/schemas/vendorSchema";
import { useVendors } from "../../hooks/useVendors";
import {
  setIsEditVendor,
  setOpenAddVendorDialog,
  setVendorDetails,
} from "../../store/slices/vendorSlice";
import { useParams } from "react-router-dom";
import { codelistUtils } from "../../utils/codelistUtils";

const VendorDialog: React.FunctionComponent<any> = (props) => {
  const { getAllVendorsFunc, paginationModel, filterVal, codelistData } = props;
  const dispatch = useAppDispatch();
  const openAddVendorDialog = useAppSelector(
    (state: AppState) => state.vendorSlice.openAddVendorDialog
  );
  const params = useParams();
  const VendorData = useAppSelector(
    (state: AppState) => state.vendorSlice.vendorDetails
  );
  const isEditVendor = useAppSelector(
    (state: AppState) => state.vendorSlice.isEditVendor
  );
  const values = isEditVendor && {
    ...VendorData,
    country: {
      code_list_value: codelistData?.length>0 && codelistUtils(codelistData,VendorData?.country_code,VendorData?.country_value_code)?.selectedValue,
      code_list_value_code: VendorData?.country_value_code,
    },
  };
  const {
    createVendor,
    createVendorData,
    createVendorIsError,
    createVendorError,
    createVendorIsSuccess,
    updateVendor,
    updateVendorData,
    updateVendorIsError,
    updateVendorError,
    updateVendorIsSuccess,
    getVendorDetails,
  } = useVendors();
  const { showSuccess, handleError } = useSnackbar();
  type FormValues = z.infer<typeof vendorSchema>;
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    setFocus,
  } = useForm<FormValues>({
    values,
    resolver: zodResolver(vendorSchema),
    defaultValues: {},
  });

  const onInvalid = (errors: any) => console.debug(errors);
  const submit = (data: any) => {
    isEditVendor ? handleUpdateVendor(data) : handleCreateVendor(data);
  };
  const handleCreateVendor = (vendor: any) => {
    createVendor({
      data: {
        ...vendor,
        study_id: params?.study_id,
        country: vendor?.country?.code_list_value,
        country_code: FormFields?.CNTRY,
        country_value_code: vendor?.country?.code_list_value_code,
      },
    });
  };
  const handleUpdateVendor = (vendor: any) => {
    updateVendor({
      data: {
        ...vendor,
        study_id: params?.study_id,
        country: vendor?.country?.code_list_value,
        country_code: FormFields?.CNTRY,
        country_value_code: vendor?.country?.code_list_value_code,
      },
    });
  };
  const onCloseVendorDialog = () => {
    dispatch(setOpenAddVendorDialog(false));
    dispatch(setIsEditVendor(false));
  };
  useEffect(() => {
    const firstError = Object.keys(errors)?.reduce((field: any, a) => {
      return (errors as any)[field] ? field : a;
    }, null);
    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);
  useEffect(() => {
    if (createVendorIsSuccess) {
      const successMsg = createVendorData;
      getAllVendorsFunc(0, paginationModel.pageSize, filterVal);
      showSuccess(successMsg?.details, "vendor-create-success");
      onCloseVendorDialog();
    }
  }, [createVendorIsSuccess]);
  useEffect(() => {
    if (updateVendorIsSuccess) {
      (async () => {
        const res = await getVendorDetails({
          study_id: params?.study_id,
          vendor_id: VendorData?.vendor_id,
        });
        if (res?.data) {
          dispatch(setVendorDetails(res?.data));
        }
      })();
      const successMsg = updateVendorData;
      showSuccess(successMsg?.details, "vendor-update-success");
      getAllVendorsFunc(0, paginationModel.pageSize, filterVal);
      onCloseVendorDialog();
    }
  }, [updateVendorIsSuccess]);
  useEffect(() => {
    if (createVendorIsError || updateVendorIsError) {
      const errorMsg: any = createVendorError ?? updateVendorError;
      handleError(errorMsg);
    }
  }, [createVendorIsError, updateVendorIsError]);
  return (
    <AlertDialog
      open={openAddVendorDialog}
      title="Vendor Details"
      handleClose={onCloseVendorDialog}
      handleConfirm={handleSubmit(submit, onInvalid)}
      maxWidth="md"
    >
      <form onSubmit={handleSubmit(submit, onInvalid)}>
        <DynamicForm
          fieldsJson={FormFields?.vendorFields}
          errors={errors}
          register={register}
          control={control}
        />
      </form>
    </AlertDialog>
  );
};

export default VendorDialog;
